<template>
    <div class="tpk" :class="{ 'is-open': open }">
        <button ref="trigger" type="button" class="tpk__trigger" :class="{ 'is-active': !isDefault }"
            aria-haspopup="listbox" :aria-expanded="open" @click="toggleOpen">
            <span class="tpk__trigger-label">{{ summary }}</span>
            <span v-if="selectedCount > 1" class="tpk__trigger-count">{{ selectedCount }}</span>
            <angle-down-icon class="tpk__chevron" />
        </button>
        <div v-if="open" class="tpk__menu" role="listbox" aria-multiselectable="true">
            <button v-for="opt in options" :key="opt.id" type="button" class="tpk__option" role="option"
                :class="{ 'is-checked': isChecked(opt.id), 'is-preset': isPreset(opt.id) }"
                :aria-selected="isChecked(opt.id)" @click="$emit('toggle', opt.id)">
                <span class="tpk__check"><check-icon /></span>
                <span class="tpk__option-label">{{ opt.label }}</span>
            </button>
        </div>
    </div>
</template>
<script>
import CheckIcon from '@/assets/images/icons/CheckIcon.svg';
import AngleDownIcon from '@/assets/images/icons/AngleDownIcon.svg';

// Multi-select popover for the transaction type filter. The parent owns the
// selection rules (e.g. "Only the good" expands to several ids); this only
// renders the list and reports which id was clicked.
export default {
    name: 'TypePicker',
    components: { CheckIcon, AngleDownIcon },
    props: {
        options: { type: Array, required: true },
        value: { type: Array, default: () => [0] },
        presetIds: { type: Array, default: () => [] },
    },
    data() {
        return { open: false };
    },
    computed: {
        selected() {
            return this.value || [];
        },
        selectedCount() {
            return this.selected.length;
        },
        isDefault() {
            return this.selected.length === 1 && this.selected[0] == 0;
        },
        summary() {
            if (this.selected.length === 0 || this.isDefault) {
                return this.options.find((o) => o.id == 0)?.label || '';
            }
            const labels = this.selected
                .map((id) => this.options.find((o) => o.id == id)?.label)
                .filter(Boolean);
            if (labels.length === 1) return labels[0];
            return labels.slice(0, 2).join(', ') + (labels.length > 2 ? '…' : '');
        },
    },
    mounted() {
        document.addEventListener('mousedown', this.onDocumentDown);
        document.addEventListener('keydown', this.onKey);
    },
    beforeDestroy() {
        document.removeEventListener('mousedown', this.onDocumentDown);
        document.removeEventListener('keydown', this.onKey);
    },
    methods: {
        isChecked(id) {
            return this.selected.some((v) => v == id);
        },
        isPreset(id) {
            return this.presetIds.some((v) => v == id);
        },
        toggleOpen() {
            this.open = !this.open;
        },
        onDocumentDown(e) {
            if (this.open && !this.$el.contains(e.target)) this.open = false;
        },
        onKey(e) {
            if (e.key === 'Escape' && this.open) {
                this.open = false;
                this.$refs.trigger?.focus();
            }
        },
    },
};
</script>
<style lang="scss">
.tpk {
    position: relative;

    &__trigger {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        height: 42px;
        max-width: 280px;
        padding: 0 12px 0 14px;
        border: 1px solid var(--gsf-border);
        border-radius: var(--gsf-radius);
        background: #fff;
        font: inherit;
        font-size: 14px;
        font-weight: 600;
        color: var(--gsf-fg);
        cursor: pointer;
        transition: border-color 120ms ease, box-shadow 120ms ease;

        &:hover { border-color: var(--gsf-border-strong); }
        &:focus-visible { outline: none; border-color: var(--gsf-primary); box-shadow: 0 0 0 3px var(--gsf-primary-ring); }

        &.is-active {
            border-color: var(--gsf-primary);
            background: var(--gsf-primary-soft);
            color: var(--gsf-primary);
        }
    }

    &.is-open &__trigger { border-color: var(--gsf-primary); box-shadow: 0 0 0 3px var(--gsf-primary-ring); }

    &__trigger-label {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__trigger-count {
        flex: 0 0 auto;
        min-width: 20px;
        padding: 1px 6px;
        border-radius: 999px;
        background: var(--gsf-primary);
        color: #fff;
        font-size: 11.5px;
        font-weight: 700;
        text-align: center;
    }

    &__chevron {
        flex: 0 0 auto;
        width: 12px;
        height: 12px;
        fill: currentColor;
        opacity: 0.7;
        transition: transform 160ms ease;
    }

    &.is-open &__chevron { transform: rotate(180deg); }

    &__menu {
        position: absolute;
        top: calc(100% + 6px);
        left: 0;
        z-index: 40;
        min-width: 260px;
        padding: 6px;
        border: 1px solid var(--gsf-border);
        border-radius: 12px;
        background: #fff;
        box-shadow: 0 16px 40px -12px rgba(24, 34, 48, 0.28), 0 0 0 1px rgba(24, 34, 48, 0.03);
    }

    &__option {
        display: flex;
        align-items: center;
        gap: 10px;
        width: 100%;
        padding: 8px 10px;
        border: 0;
        border-radius: 8px;
        background: transparent;
        font: inherit;
        font-size: 13.5px;
        color: var(--gsf-fg);
        text-align: left;
        cursor: pointer;

        &:hover { background: var(--gsf-surface); }
        &:focus-visible { outline: none; box-shadow: inset 0 0 0 2px var(--gsf-primary-ring); }

        &.is-preset { font-weight: 600; }
        &.is-preset + &:not(.is-preset) { margin-top: 4px; border-top: 1px solid var(--gsf-border); border-radius: 0 0 8px 8px; padding-top: 12px; }
        &.is-checked .tpk__option-label { font-weight: 600; }
    }

    &__check {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 auto;
        width: 18px;
        height: 18px;
        border-radius: 5px;
        border: 1.5px solid var(--gsf-border-strong);
        background: #fff;
        color: #fff;

        svg { width: 11px; height: 11px; fill: currentColor; }
    }

    &__option.is-checked &__check { background: var(--gsf-primary); border-color: var(--gsf-primary); }
}
</style>
